import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// --- Exposition (Demo / visual-only) mode ---
let _expositionMode = false;
export const setExpositionMode = (value) => { _expositionMode = !!value; };

// Endpoints allowed for demo users even though they are POSTs (reads / auth)
const EXPOSITION_ALLOWED = ["/auth/login", "/auth/logout", "/ai-assistant/chat", "/aip/query", "/apron/check-compatibility"];

const isDemoBlocked = (config) => {
  if (!_expositionMode) return false;
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";
  const isMutating = ["post", "put", "patch", "delete"].includes(method);
  const isExport = /\/export(\/|\?|$)/.test(url);
  if (!isMutating && !isExport) return false;
  if (EXPOSITION_ALLOWED.some((p) => url.includes(p))) return false;
  return true;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (isDemoBlocked(config)) {
    // Notify the global demo dialog and cancel the request
    window.dispatchEvent(new CustomEvent("demo-mode-block"));
    const err = new axios.Cancel("DEMO_MODE");
    err.__demoBlocked = true;
    return Promise.reject(err);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    // Backend-enforced demo block (e.g. direct API bypass)
    if (error.response?.status === 403 && typeof error.response?.data?.detail === "string" && error.response.data.detail.startsWith("DEMO_MODE")) {
      window.dispatchEvent(new CustomEvent("demo-mode-block"));
    }
    return Promise.reject(error);
  }
);

// Auth
export const initAdmin = () => api.post("/init-admin");

// Users
export const getUsers = () => api.get("/users");
export const createUser = (data) => api.post("/users", data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Login History
export const getLoginHistory = () => api.get("/login-history");
export const deleteLoginHistory = () => api.delete("/login-history");

// Active Sessions & Force Logout
export const getActiveSessions = () => api.get("/active-sessions");
export const logoutAllUsers = () => api.post("/logout-all-users");
export const forceLogoutUser = (userId) => api.post(`/active-sessions/${userId}/force-logout`);
export const getFailedLogins = () => api.get("/failed-logins");
export const clearFailedLogins = () => api.delete("/failed-logins");
export const getBlocklist = () => api.get("/blocklist");
export const addBlocklist = (type, value, reason) => api.post("/blocklist", { type, value, reason });
export const removeBlocklist = (id) => api.delete(`/blocklist/${id}`);
export const getBanHistory = () => api.get("/ban-history");
export const getAutoLogoutSettings = () => api.get("/auto-logout-settings");
export const updateAutoLogoutSettings = (data) => api.put("/auto-logout-settings", data);

// Flights
export const getFlights = (params) => api.get("/flights", { params });
export const createFlight = (data) => api.post("/flights", data);
export const updateFlight = (id, data) => api.put(`/flights/${id}`, data);
export const deleteFlight = (id) => api.delete(`/flights/${id}`);
export const bulkDeleteCanceledFlights = (ids) => api.post("/canceled-flights/bulk-delete", { ids });
export const cancelFlight = (id, reason) => api.post(`/flights/${id}/cancel`, { reason });
export const restoreFlight = (id) => api.post(`/flights/${id}/restore`);
export const getAircraftRotation = (registration, contextDate) => {
  const params = contextDate ? { context_date: contextDate } : {};
  return api.get(`/flights/rotation/${registration}`, { params });
};
export const getFlightDates = () => api.get("/flights/dates");
export const getFlightHistory = (id) => api.get(`/flights/${id}/history`);
export const getDerogationTemplate = () => api.get("/derogation-pdf");
export const listFlightDerogations = (id) => api.get(`/flights/${id}/derogations`);
export const getFlightDerogation = (id, docId) => api.get(`/flights/${id}/derogations/${docId}`);
export const saveFlightDerogation = (id, payload) => api.post(`/flights/${id}/derogations`, payload);
export const generateFlightDocument = (id, payload) => api.post(`/flights/${id}/documents`, payload);
export const generateFlightBriefing = (id, payload) => api.post(`/flights/${id}/briefing`, payload);
export const getFlightAlternates = (id, limit = 3) => api.get(`/flights/${id}/nearby-alternates`, { params: { limit } });
export const listBriefingPresets = () => api.get("/briefing-presets");
export const saveBriefingPreset = (payload) => api.post("/briefing-presets", payload);
export const deleteBriefingPreset = (id) => api.delete(`/briefing-presets/${id}`);
export const listFlightDocuments = (id) => api.get(`/flights/${id}/documents`);
export const getFlightDocument = (id, docId) => api.get(`/flights/${id}/documents/${docId}`);
export const getFlightDocTimes = (id) => api.get(`/flights/${id}/doc-times`);
export const deleteFlightDocument = (id, docId) => api.delete(`/flights/${id}/documents/${docId}`);

// Tomorrow planning
export const getTomorrowPlanning = (opts) => api.post("/planning/tomorrow", opts || {});
export const getPlanningRecipients = () => api.get("/planning/recipients");
export const updatePlanningRecipients = (recipients) => api.put("/planning/recipients", { recipients });
export const sendTomorrowPlanning = (recipients, saveDefault, opts) =>
  api.post("/planning/send", { recipients, save_default: !!saveDefault, ...(opts || {}) });
export const recordPlanningArchive = (payload) => api.post("/planning/archive", payload || {});
export const getPlanningArchive = () => api.get("/planning/archive");

// Statistics
export const getDashboardStats = (year) => api.get("/statistics/dashboard", { params: year ? { year } : {} });
export const getMonthlyStats = (year, params = {}) => api.get("/statistics/monthly", { params: { year, ...params } });
export const getOperatorStats = (params = {}) => api.get("/statistics/operators", { params });
export const getFlightTypeStats = (params = {}) => api.get("/statistics/flight-types", { params });
export const getAircraftTypeStats = (params = {}) => api.get("/statistics/aircraft-types", { params });
export const getRoutesStats = (params = {}) => api.get("/statistics/routes", { params });
export const getServiceStats = (params = {}) => api.get("/statistics/services", { params });
export const getApronUsageStats = (params = {}) => api.get("/statistics/apron-usage", { params });
export const getAircraftTypeApronStats = (params = {}) => api.get("/statistics/aircraft-type-apron", { params });
export const getContinentStats = (params = {}) => api.get("/statistics/continents", { params });
export const getProximityStats = (radiusKm = 500, params = {}) => api.get("/statistics/proximity", { params: { radius_km: radiusKm, ...params } });
export const getGeoStats = (params = {}) => api.get("/statistics/geo", { params });
export const getCargoStats = (params = {}) => api.get("/statistics/cargo", { params });
export const getDelayStats = (params = {}) => api.get("/statistics/delays", { params });
export const getAvailableYears = () => api.get("/statistics/available-years");

// Settings
export const getSettings = () => api.get("/settings");
export const updateSettings = (data) => api.put("/settings", data);
export const uploadLogo = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/settings/logo", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
};

export const uploadApronMap = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/settings/apron-map", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
};

export const updateApronMapTransform = (data) => api.put("/settings/apron-map-transform", data);

export const deleteApronMap = () => api.delete("/settings/apron-map");

// General Notes
export const getGeneralNotes = () => api.get("/general-notes");
export const updateGeneralNotes = (content) => api.put("/general-notes", { content });

// Operational Notes
export const getOperationalNotes = () => api.get("/operational-notes");
export const updateOperationalNotes = (content) => api.put("/operational-notes", { content });
export const getOperationalNotesFromFlights = () => api.get("/operational-notes/from-flights");
export const getPastOperationalNotesFromFlights = () => api.get("/operational-notes/past-flights");

// Backup
export const exportBackup = () => api.get("/backup/export");
export const exportPdfsBackup = () => api.get("/backup/export-pdfs");
export const importBackup = (data) => api.post("/backup/import", data);

// System Backups (Automatic Backup System - Master Admin only)
export const getSystemBackups = () => api.get("/system-backups");
export const restoreSystemBackup = (id) => api.post(`/system-backups/${id}/restore`);
export const deleteSystemBackup = (id) => api.delete(`/system-backups/${id}`);
export const createSystemBackupNow = () => api.post("/system-backups/create-now");
export const getBackupStatus = () => api.get("/system-backups/status/info");

// Aircraft Data
export const lookupAircraft = (registration) => api.get(`/aircraft-data/lookup/${registration}`);
export const uploadAircraftData = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/aircraft-data/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
};
export const deleteAircraftRecord = (id) => api.delete(`/aircraft-data/${id}`);
export const getAircraftFlights = (id) => api.get(`/aircraft-data/${id}/flights`);
export const getDuplicateAircraft = () => api.get("/aircraft-data/duplicates");
export const mergeAircraftRegistrations = (payload) => api.post("/aircraft-data/merge-registrations", payload);
export const deleteAllAircraftData = () => api.delete("/aircraft-data");

// Airport Data
export const getUnrecognizedAirportCodes = () => api.get("/airport-data/unrecognized");
export const syncAirportToFlights = (icaoCode) => api.post("/airport-data/sync-flights", { icao_code: icaoCode });
export const lookupAirport = (icaoCode) => api.get(`/airport-data/lookup/${icaoCode}`);

// Refresh Schengen/EU Status
export const refreshSchengenEuStatus = () => api.post("/refresh-schengen-eu-status");

// Weather & NOTAMs
export const getEBCIWeather = () => api.get("/weather/ebci");
export const getEBCINotams = () => api.get("/notams/ebci");
export const getEBCIRadar = () => api.get("/radar/ebci");
export const getFlightTrail = (flightId) => api.get(`/radar/trail/${flightId}`);

// System Health (Login Watchdog)
export const pingHealth = () => api.get("/health");
export const getHealthSummary = () => api.get("/health/summary");
export const getLoginErrors = (limit = 50) => api.get(`/health/login-errors?limit=${limit}`);

// Flight Tracking - supports both registration and flight number for better PlaneFinder tracking
export const getBatchFlightTracking = (registrations, flightNumbers = null) => {
  let url = `/flight-tracking-batch?registrations=${registrations}`;
  if (flightNumbers) {
    url += `&flight_numbers=${encodeURIComponent(flightNumbers)}`;
  }
  return api.get(url);
};
export const getLiveTracking = () => api.get("/flight-tracking/live");
export const triggerTrackingPoll = () => api.post("/flight-tracking/poll-now");

// Permissions & Roles
export const getRoles = () => api.get("/roles");
export const getRoleHierarchy = () => api.get("/role-hierarchy");
export const createRole = (data) => api.post("/roles", data);
export const updateRole = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole = (id) => api.delete(`/roles/${id}`);
export const getUserPermissions = (userId) => api.get(`/user-permissions/${userId}`);
export const updateUserPermissions = (userId, data) => api.put(`/user-permissions/${userId}`, data);
export const getMyPermissions = () => api.get("/my-permissions");

// Services Management
export const getServices = () => api.get("/services");

// Aircraft Specifications
export const getAircraftSpecs = (aircraftType) => api.get(`/aircraft-specs/${encodeURIComponent(aircraftType)}`);
export const getApronCompatibility = (aircraftType) => api.get(`/apron/compatibility-summary/${encodeURIComponent(aircraftType)}`);
export const getApronCompatibilityBatch = (aircraftTypes) => api.post(`/apron/compatibility-batch`, { aircraft_types: aircraftTypes });

// AI Aviation Assistant
export const sendAIMessage = (message, sessionId = null) => 
  api.post("/ai-assistant/chat", { message, session_id: sessionId });
export const getAIChatHistory = (sessionId) => api.get(`/ai-assistant/history/${sessionId}`);
export const getAISessions = () => api.get("/ai-assistant/sessions");
export const deleteAISession = (sessionId) => api.delete(`/ai-assistant/session/${sessionId}`);

// Apron Management
export const getAircraftOnGround = (date = null) => 
  api.get("/apron/aircraft-on-ground", { params: date ? { date } : {} });
export const getParkingStands = () => api.get("/apron/stands");
export const checkStandCompatibility = (stand, aircraftType) => 
  api.post("/apron/check-compatibility", { stand, aircraft_type: aircraftType });
export const updateAircraftParking = (flightId, parkingPosition) => 
  api.put(`/apron/update-parking/${flightId}`, { parking_position: parkingPosition });

// Parking Configuration
export const getParkingConfig = () => api.get("/parking-config");
export const saveParkingConfig = (config) => api.post("/parking-config", config);
export const deleteParkingConfig = (standId) => api.delete(`/parking-config/${standId}`);

// Parking Configuration Backup
export const exportParkingBackup = () => api.get("/parking-config/backup/export");
export const importParkingBackup = (data) => api.post("/parking-config/backup/import", data);
export const getParkingBackups = () => api.get("/parking-config/backups");
export const saveParkingBackup = (name) => api.post("/parking-config/backup/save", { name });
export const restoreParkingBackup = (backupId) => api.post(`/parking-config/backup/${backupId}/restore`);
export const deleteParkingBackup = (backupId) => api.delete(`/parking-config/backup/${backupId}`);

// Historical Parking Data
export const getHistoricalParking = (date) => api.get("/apron/historical-parking", { params: { date } });

// Manual Aircraft on Apron
export const createManualAircraft = (data) => api.post("/apron/manual-aircraft", data);
export const deleteManualAircraft = (id) => api.delete(`/apron/manual-aircraft/${id}`);
export const updateManualAircraftStand = (id, stand) => api.patch(`/apron/manual-aircraft/${id}`, { stand });
export const updateManualAircraft = (id, data) => api.patch(`/apron/manual-aircraft/${id}`, data);

// Aircraft / Airport export & merge
export const mergeAircraftOperator = (aircraftIds, targetOperator) =>
  api.post("/aircraft-data/merge-operator", { aircraft_ids: aircraftIds, target_operator: targetOperator });
export const getAircraftMissingInfo = () => api.get("/aircraft-data/missing-info");
export const getAircraftMissingList = (field) => api.get("/aircraft-data/missing-info/list", { params: { field } });
export const exportAircraftDataXlsx = () => api.get("/aircraft-data/export", { responseType: "blob" });
export const exportAirportDataXlsx = () => api.get("/airport-data/export", { responseType: "blob" });

// Stand Closure Scheduler
export const getStandClosures = () => api.get("/stand-closures");
export const createStandClosure = (data) => api.post("/stand-closures", data);
export const updateStandClosure = (closureId, data) => api.put(`/stand-closures/${closureId}`, data);
export const deleteStandClosure = (closureId) => api.delete(`/stand-closures/${closureId}`);

export const getOperationalRestrictions = () => api.get("/operational-restrictions");
export const createOperationalRestriction = (data) => api.post("/operational-restrictions", data);
export const updateOperationalRestriction = (id, data) => api.put(`/operational-restrictions/${id}`, data);
export const deleteOperationalRestriction = (id) => api.delete(`/operational-restrictions/${id}`);

// AIP Information System
export const refreshAIPData = () => api.post("/aip/refresh");
export const getAIPSettings = () => api.get("/aip/settings");
export const updateAIPSettings = (aipUrl) => api.put("/aip/settings", { aip_url: aipUrl });

// Email Integration
export const getEmailSettings = () => api.get("/email-settings");
export const saveEmailSettings = (data) => api.post("/email-settings", data);
export const testEmailConnection = () => api.post("/email/test-connection");
export const testSendEmail = () => api.post("/email/test-send");
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email });
export const resetPassword = (email, code, new_password, method = "email") => api.post("/auth/reset-password", { email, code, new_password, method });
export const selfUnban = (email, code) => api.post("/auth/self-unban", { email, code });
export const getTotpStatus = () => api.get("/totp/status");
export const totpEnrollStart = () => api.post("/totp/enroll/start");
export const totpEnrollVerify = (code) => api.post("/totp/enroll/verify", { code });
export const totpDisable = (password) => api.post("/totp/disable", { password });
export const pollEmailsNow = () => api.post("/email/poll-now");
export const getPendingFlights = () => api.get("/email/pending-flights");
export const getAllPendingFlights = () => api.get("/email/pending-flights/all");
export const getEmailHistory = () => api.get("/email/history");
export const deleteEmailHistoryItem = (id) => api.delete(`/email/history/${id}`);
export const deleteAllEmailHistory = () => api.delete("/email/history");
export const approvePendingFlight = (id, flightData) => api.post(`/email/pending-flights/${id}/approve`, { flight_data: flightData });
export const rejectPendingFlight = (id, reason) => api.post(`/email/pending-flights/${id}/reject`, { reason });
export const deletePendingFlight = (id) => api.delete(`/email/pending-flights/${id}`);
export const deleteAllPendingFlights = () => api.delete("/email/pending-flights");
export const submitManualEmail = (data) => api.post("/email/submit-manual", data);
export const uploadPdfForParsing = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/email/upload-pdf", formData, { headers: { "Content-Type": "multipart/form-data" } });
};

// LVO Management
export const getLvoStatus = () => api.get("/lvo/status");
export const respondToLvo = (id, action) => api.post(`/lvo/notifications/${id}/respond`, { action });
export const manualLvoToggle = (activate, category) => api.post("/lvo/manual-toggle", { activate, category });

// ==================== CARGO MODULE ====================
export const getCargoModuleStats = () => api.get("/cargo/dashboard/stats");
export const getCargoWeights = (year) => api.get("/cargo/dashboard/weights", { params: year ? { year } : {} });
export const getCargoBreakdown = (params = {}) => api.get("/cargo/dashboard/breakdown", { params });
export const getCargoMonthly = (params = {}) => api.get("/cargo/dashboard/monthly", { params });
export const listCargoEntries = (params = {}) => api.get("/cargo/entries", { params });
export const getCargoEntry = (id) => api.get(`/cargo/entries/${id}`);
export const createCargoEntry = (data) => api.post("/cargo/entries", data);
export const updateCargoEntry = (id, data) => api.put(`/cargo/entries/${id}`, data);
export const deleteCargoEntry = (id) => api.delete(`/cargo/entries/${id}`);
export const uploadCargoExcel = (file, mode = "append") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", mode);
  return api.post("/cargo/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const bulkCargoStatus = (ids, status) => api.post("/cargo/entries/bulk-status", { ids, status });
export const setEntryServices = (id, services) => api.put(`/cargo/entries/${id}/services`, { services });
export const entriesForInvoice = (agency, month, party) => api.get("/cargo/entries-for-invoice", { params: { agency, month: month || undefined, party: party || undefined } });
export const listCargoInvoices = () => api.get("/cargo/invoices");
export const createCargoInvoice = (data) => api.post("/cargo/invoices", data);
export const deleteCargoInvoice = (id) => api.delete(`/cargo/invoices/${id}`);
export const downloadCargoInvoiceXlsx = (id) => api.get(`/cargo/invoices/${id}/xlsx`, { responseType: "blob" });
export const listCargoServices = (year, fallback) => api.get("/cargo/services", { params: { ...(year ? { year } : {}), ...(fallback ? { fallback: true } : {}) } });
export const createCargoService = (data) => api.post("/cargo/services", data);
export const updateCargoService = (id, data) => api.put(`/cargo/services/${id}`, data);
export const deleteCargoService = (id) => api.delete(`/cargo/services/${id}`);
export const copyCargoService = (id, party) => api.post(`/cargo/services/${id}/copy`, { party });
export const copyCargoServicesToYear = (from_year, to_year, percent) => api.post("/cargo/services/copy-to-year", { from_year, to_year, percent });

// ===== Financial statistics =====
export const getFinancialStats = () => api.get("/financial/stats");
export const upsertFinancialYear = (year, months) => api.put(`/financial/stats/${year}`, { months });
export const addFinancialYear = (year, months) => api.post("/financial/stats/year", { year, months });
export const deleteFinancialYear = (year) => api.delete(`/financial/stats/${year}`);
export const getFinancialConfig = () => api.get("/financial/config");
export const updateFinancialConfig = (growth_target_pct) => api.put("/financial/config", { growth_target_pct });
export const listCargoAgencies = () => api.get("/cargo/agencies");
export const createCargoAgency = (data) => api.post("/cargo/agencies", data);
export const updateCargoAgency = (id, data) => api.put(`/cargo/agencies/${id}`, data);
export const deleteCargoAgency = (id) => api.delete(`/cargo/agencies/${id}`);

// Cargo In-Flight (daily flights + cargo booking)
export const listCargoFlights = (params = {}) => api.get("/cargo/flights", { params });
export const getCargoFlightSchedule = (params) => api.get("/cargo/flights/schedule", { params });
export const getUpcomingCargoFlights = (params = {}) => api.get("/cargo/flights/upcoming", { params });
export const splitEntryAcrossFlights = (eid, allocations) => api.post(`/cargo/entries/${eid}/split`, { allocations });
export const fixCargoFlight = (data) => api.post("/cargo/flights", data);
export const updateCargoFlight = (id, data) => api.put(`/cargo/flights/${id}`, data);
export const setCargoFlightEntries = (id, cargoEntryIds) => api.put(`/cargo/flights/${id}/cargo`, { cargo_entry_ids: cargoEntryIds });
export const deleteCargoFlight = (id) => api.delete(`/cargo/flights/${id}`);
export const getCargoFlightAssignments = () => api.get("/cargo/flights/assignments");
export const buildFlightsFromCargo = (opts = {}) => api.post("/cargo/flights/build-from-cargo", opts);
export const enrichCargoFlights = (opts = {}) => api.post("/cargo/flights/enrich", opts);
export const importCargoFlightInfo = (formData) => api.post("/cargo/flights/import-info", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getFlightCargo = (id) => api.get(`/cargo/flights/${id}/cargo`);
export const releaseFlightCargo = (id, entryId) => api.post(`/cargo/flights/${id}/release`, { entry_id: entryId });
export const splitFlightCargo = (id, payload) => api.post(`/cargo/flights/${id}/split`, payload);
export const generateCargoManifest = (id, payload) => api.post(`/cargo/flights/${id}/manifest`, payload, { responseType: "blob" });
export const generateCargoTags = (id, payload) => api.post(`/cargo/flights/${id}/tags`, payload, { responseType: "blob" });
export const saveCargoManifestData = (id, data) => api.put(`/cargo/flights/${id}/manifest-data`, data);
export const saveCargoTagsData = (id, data) => api.put(`/cargo/flights/${id}/tags-data`, data);

// Cargo Backup (Master Admin only)
export const exportCargoBackup = () => api.get("/cargo/backup/export");
export const importCargoBackup = (data) => api.post("/cargo/backup/import", data);
export const getCargoSystemBackups = () => api.get("/cargo/system-backups");
export const createCargoBackupNow = () => api.post("/cargo/system-backups/create-now");
export const restoreCargoSystemBackup = (id) => api.post(`/cargo/system-backups/${id}/restore`);
export const deleteCargoSystemBackup = (id) => api.delete(`/cargo/system-backups/${id}`);
export const getCargoBackupStatus = () => api.get("/cargo/system-backups/status/info");

export default api;
